import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { MonitorSmartphone, Smartphone, Monitor, LogOut, EyeOff } from "lucide-react";

const when = (iso) => new Date(iso).toLocaleString();

function DeviceRow({ s, onAction }) {
  const Icon = s.device.startsWith("Mobile") ? Smartphone : Monitor;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border px-4 py-3" data-testid={`device-row-${s.sid}`}>
      <Icon size={20} className="text-primary" />
      <div className="min-w-0 basis-[calc(100%-2.5rem)] sm:basis-auto sm:flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          {s.device}
          {s.current && <Badge className="border-emerald-500/40 text-emerald-500" data-testid="device-current-badge">This device</Badge>}
          {s.trusted && <Badge className="border-primary/40 text-primary">Trusted</Badge>}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">Network {s.ip} · signed in {when(s.created_at)} · last active {when(s.last_seen)}</div>
      </div>
      <div className="ml-auto flex gap-2">
      {s.trusted && (
        <Btn size="sm" variant="outline" onClick={() => onAction(s, "forget")} data-testid={`device-forget-${s.sid}`} title="Next sign-in from here will alert the Manager">
          <EyeOff size={13} /> Forget
        </Btn>
      )}
      <Btn size="sm" variant="outline" className="text-red-500" onClick={() => onAction(s, "signout")} data-testid={`device-signout-${s.sid}`}>
        <LogOut size={13} /> Sign out
      </Btn>
      </div>
    </div>
  );
}

export default function Devices() {
  const { logout } = useAuth();
  const [data, setData] = useState({ sessions: [] });
  const load = () => { api.get("/auth/devices").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const onAction = async (s, kind) => {
    if (kind === "signout" && s.current && !window.confirm("Sign out this device? You'll need to sign in again.")) return;
    try {
      await api.post(`/auth/devices/${s.sid}/${kind}`);
      if (kind === "signout" && s.current) { logout(); return; }
      toast.success(kind === "signout" ? "Device signed out" : "Device forgotten. Its next sign-in will be flagged.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="devices-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl"><MonitorSmartphone size={24} /> My Devices</h1>
        <p className="text-sm text-muted-foreground">Devices signed in to your account. <b>Sign out</b> ends that session right away; <b>Forget</b> only means the Manager gets a new-device alert next time it signs in.</p>
      </div>
      <Card className="space-y-2 p-3">
        {data.sessions.map((s) => <DeviceRow key={s.sid} s={s} onAction={onAction} />)}
        {!data.sessions.length && <div className="p-4 text-center text-sm text-muted-foreground">No active devices.</div>}
      </Card>
    </div>
  );
}
